import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("timeout");
        }
      } catch {
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    poll();
  }, [sessionId, attempts]);

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="text-center max-w-md animate-fade-in-up opacity-0 stagger-1">
        {status === "checking" && (
          <>
            <div className="w-16 h-16 border-2 border-[#2C5545] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-medium text-[#1A1C1A] mb-2" data-testid="payment-checking">Processing payment...</h1>
            <p className="text-sm text-[#5C665D]">Please wait while we confirm your payment.</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-[#3B7D5A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} weight="duotone" className="text-[#3B7D5A]" />
            </div>
            <h1 className="font-heading text-3xl font-medium text-[#1A1C1A] mb-3" data-testid="payment-success">Booking confirmed!</h1>
            <p className="text-sm text-[#5C665D] mb-8">Your payment was successful. The owner has been notified.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/dashboard" className="bg-[#2C5545] text-white hover:bg-[#224235] rounded-full px-6 py-3 font-medium transition-all inline-flex items-center gap-2 justify-center" data-testid="success-dashboard-link">
                View Bookings <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/browse" className="bg-white text-[#1A1C1A] border border-[#EAE7E0] hover:border-[#2C5545] rounded-full px-6 py-3 font-medium transition-all inline-block" data-testid="success-browse-link">
                Continue Browsing
              </Link>
            </div>
          </>
        )}
        {(status === "error" || status === "expired" || status === "timeout") && (
          <>
            <h1 className="font-heading text-2xl font-medium text-[#1A1C1A] mb-3" data-testid="payment-error">
              {status === "expired" ? "Payment expired" : "Payment issue"}
            </h1>
            <p className="text-sm text-[#5C665D] mb-6">
              {status === "expired" ? "Your payment session has expired." : "We couldn't confirm your payment. Please check your dashboard."}
            </p>
            <Link to="/dashboard" className="bg-[#2C5545] text-white hover:bg-[#224235] rounded-full px-6 py-3 font-medium transition-all inline-flex items-center gap-2" data-testid="error-dashboard-link">
              Go to Dashboard <ArrowRight size={16} weight="bold" />
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
